import type { KeyboardEvent, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'

export interface SegmentedOption<T extends string> {
    value: T
    label: string
}

interface SegmentedControlProps<T extends string> {
    id?: string
    options: SegmentedOption<T>[]
    value: T
    onChange: (value: T) => void
    ariaLabel: string
    className?: string
    allowDeselect?: boolean
    emptyValue?: T
}

const SegmentedControl = <T extends string>({
    id,
    options,
    value,
    onChange,
    ariaLabel,
    className,
    allowDeselect = false,
    emptyValue
}: SegmentedControlProps<T>): ReactNode => {
    const buttonsRef = useRef<Array<HTMLButtonElement | null>>([])

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return

        event.preventDefault()
        const direction = event.key === 'ArrowRight' ? 1 : -1
        const nextIndex = (index + direction + options.length) % options.length

        onChange(options[nextIndex].value)
        buttonsRef.current[nextIndex]?.focus()
    }

    const activeIndex = options.findIndex(option => option.value === value)

    return (
        <div
            id={id}
            role='radiogroup'
            aria-label={ariaLabel}
            className={cn('inline-flex w-full rounded-lg border border-input bg-background p-0.5', className)}
        >
            {options.map((option, index) => {
                const active = option.value === value
                const focusable = active || (activeIndex === -1 && index === 0)

                return (
                    <button
                        key={option.value}
                        ref={element => {
                            buttonsRef.current[index] = element
                        }}
                        type='button'
                        role='radio'
                        aria-checked={active}
                        tabIndex={focusable ? 0 : -1}
                        onKeyDown={event => handleKeyDown(event, index)}
                        onClick={() => {
                            if (active && allowDeselect && emptyValue !== undefined) onChange(emptyValue)
                            else onChange(option.value)
                        }}
                        className={cn(
                            'h-8 flex-1 rounded-md px-3 text-sm font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/40',
                            active ? 'bg-primary text-primary-foreground shadow-xs' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        )}
                    >
                        {option.label}
                    </button>
                )
            })}
        </div>
    )
}

export default SegmentedControl
